/**
 * Cardurile de restaurant.
 *
 * Un card raspunde, inainte de orice apasare, la intrebarile pe care si le pune
 * clientul unei platforme de livrare: ce fel de mancare, in cat timp, cat costa
 * livrarea, cat e minimul, si — cand adresa e cunoscuta — DACA ajunge la el.
 * Fara ele, cardul era o poza cu un nume si un „Vezi meniul", iar toate
 * raspunsurile veneau abia in meniu, dupa un clic care putea fi degeaba.
 *
 * Datele „de la" vin din listarea publicata in bootstrap (orientative, fara
 * adresa); cele exacte, din acoperirea pe adresa. Cand niciuna nu exista (core
 * mai vechi), cardul ramane nume + poza — nu inventeaza nimic.
 *
 * ADR-003: alegerea VANZATORULUI nu se face aici. Pe o vitrina cu meniu
 * partajat, cardul e restaurantul asa cum il vede clientul; ce bucatarie
 * gateste efectiv se stabileste la finalizare.
 */

import type { StorefrontCoverageEntryDto, StorefrontSummaryDto } from "@deliveryapp/shared";
import { formatRon } from "@deliveryapp/shared";

import { IconBike, IconChevronRight, IconClock, IconTag } from "./icons";
import { ImageFallback, ResponsiveImage } from "./Media";
import { storefrontCardFacts } from "./storefront-listing";
import { Badge } from "./ui";

/**
 * Aranjarile pe care le poate cere blocul „Restaurante" din builder. Clasele nu
 * se pot construi din bucati la runtime: Tailwind isi citeste continutul static.
 */
const GRID_COLUMNS: Record<string, string> = {
  "2": "sm:grid-cols-2",
  "3": "sm:grid-cols-2 lg:grid-cols-3",
  "4": "sm:grid-cols-2 lg:grid-cols-4",
};

export function StorefrontCard({
  storefront,
  coverage,
  coverageState,
  onSelect,
  priority = false,
  layout = "grila",
}: {
  storefront: StorefrontSummaryDto;
  coverage: StorefrontCoverageEntryDto | null;
  coverageState: "unknown" | "loading" | "ready";
  onSelect: (slug: string) => void;
  priority?: boolean;
  layout?: "grila" | "carusel" | "lista";
}) {
  const facts = storefrontCardFacts(storefront, coverage);
  const previews = storefront.listing?.previewImageUrls ?? [];
  const hero = storefront.heroImageUrl ?? previews[0] ?? null;
  const dimmed = facts.coverage === "out_of_coverage" || facts.coverage === "no_vendor" || !facts.acceptingOrders;
  const row = layout === "lista";

  return (
    <li className={`da-card da-card-link overflow-hidden ${dimmed ? "opacity-80" : ""}`}>
      <button
        type="button"
        onClick={() => onSelect(storefront.slug)}
        className={`da-tap flex w-full text-left ${row ? "flex-row" : "flex-col"}`}
        aria-label={`${storefront.name}${facts.eta === null ? "" : `, ${facts.eta}`}${facts.coverage === "deliverable" ? ", livrează la adresa ta" : facts.coverage === null ? "" : ", nu livrează la adresa ta"}`}
      >
        <div className={`relative shrink-0 ${row ? "w-32 self-stretch sm:w-44" : "w-full"}`}>
          {hero !== null || previews.length === 0 ? (
            <ResponsiveImage
              src={hero}
              alt=""
              ratio={row ? "1/1" : "16/9"}
              rounded={false}
              priority={priority}
              sizes={row ? "11rem" : "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"}
              className={row ? "h-full" : ""}
              fallback={<ImageFallback />}
            />
          ) : null}

          {/* Etichete peste poza: doar ce schimba decizia. */}
          <div className="absolute left-2 top-2 flex flex-wrap gap-1">
            {facts.promoCount > 0 && facts.acceptingOrders ? (
              <Badge tone="promo">
                <IconTag size={12} />
                {facts.promoCount === 1 ? "1 ofertă" : `${facts.promoCount} oferte`}
              </Badge>
            ) : null}
          </div>
          {!facts.acceptingOrders ? (
            <div className="absolute inset-0 grid place-items-center bg-black/45">
              <span className="rounded-full bg-canvas px-3 py-1 text-xs font-bold text-ink">Pauză temporară</span>
            </div>
          ) : facts.coverage === "out_of_coverage" ? (
            <div className="absolute inset-0 grid place-items-center bg-black/40">
              <span className="rounded-full bg-canvas px-3 py-1 text-xs font-bold text-ink">Nu livrează la adresa ta</span>
            </div>
          ) : facts.coverage === "no_vendor" ? (
            <div className="absolute inset-0 grid place-items-center bg-black/40">
              <span className="rounded-full bg-canvas px-3 py-1 text-xs font-bold text-ink">Indisponibil acum la adresa ta</span>
            </div>
          ) : null}
          {facts.eta === null || !row ? null : null}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-3.5">
          <div className="flex items-start justify-between gap-2">
            <span className="da-clamp-1 text-base font-bold text-ink">{storefront.name}</span>
            {facts.eta === null || !facts.acceptingOrders || facts.coverage === "out_of_coverage" ? null : (
              <span
                className={[
                  "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums",
                  facts.etaIsForAddress ? "bg-brand-soft text-brand-on-surface" : "bg-surface-raised text-ink",
                ].join(" ")}
                title={facts.etaIsForAddress ? "Estimare pentru adresa ta" : "Estimare orientativă"}
              >
                <IconClock size={13} />
                {facts.eta}
              </span>
            )}
          </div>

          {facts.tags.length === 0 ? (
            facts.offerCount > 0 ? <span className="text-sm text-ink-muted">{`${facts.offerCount} produse`}</span> : null
          ) : (
            <span className="da-clamp-1 text-sm text-ink-muted">{facts.tags.join(" · ")}</span>
          )}

          <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-muted">
            {facts.fee === null ? null : (
              <span className="inline-flex items-center gap-1">
                <IconBike size={16} />
                {facts.fee.amount === 0
                  ? "Livrare gratuită"
                  : `${facts.fee.from ? "de la " : ""}${formatRon(facts.fee.amount)}`}
              </span>
            )}
            {facts.minOrder === null || facts.minOrder <= 0 ? null : <span>{`min. ${formatRon(facts.minOrder)}`}</span>}
          </span>

          {facts.freeDeliveryFrom === null || facts.freeDeliveryFrom <= 0 ? null : (
            <span className="text-xs font-semibold text-success">{`Livrare gratuită peste ${formatRon(facts.freeDeliveryFrom)}`}</span>
          )}

          {coverageState === "loading" ? (
            <span className="da-shimmer mt-0.5 block h-3.5 w-2/5 rounded" aria-hidden="true" />
          ) : facts.coverage === "deliverable" ? (
            <span className="mt-0.5 inline-flex items-center gap-1 text-xs font-bold text-success">
              <span aria-hidden="true">✓</span> Livrează la adresa ta
            </span>
          ) : null}
        </div>
      </button>
    </li>
  );
}

/** Cardul mare al unei piete cu UN singur restaurant: nu e o alegere, e o usa. */
export function SingleStorefrontCard({
  storefront,
  coverage,
  coverageState,
  onSelect,
}: {
  storefront: StorefrontSummaryDto;
  coverage: StorefrontCoverageEntryDto | null;
  coverageState: "unknown" | "loading" | "ready";
  onSelect: (slug: string) => void;
}) {
  const facts = storefrontCardFacts(storefront, coverage);
  const previews = storefront.listing?.previewImageUrls ?? [];
  const hero = storefront.heroImageUrl ?? null;

  return (
    <button
      type="button"
      onClick={() => onSelect(storefront.slug)}
      className="da-card da-card-link da-tap flex w-full flex-col overflow-hidden text-left sm:flex-row"
    >
      {hero !== null ? (
        <div className="w-full sm:w-2/5">
          <ResponsiveImage src={hero} alt="" ratio="16/9" rounded={false} priority sizes="(min-width: 640px) 40vw, 100vw" className="h-full" />
        </div>
      ) : previews.length > 0 ? (
        <div className="grid w-full grid-cols-3 gap-0.5 sm:w-2/5">
          {previews.slice(0, 3).map((url, index) => (
            <ResponsiveImage key={url} src={url} alt="" ratio="1/1" rounded={false} priority={index === 0} sizes="33vw" />
          ))}
        </div>
      ) : null}
      <div className="flex flex-1 flex-col gap-2 p-4 sm:p-5">
        <span className="text-xl font-extrabold text-ink sm:text-2xl">{storefront.name}</span>
        {facts.tags.length === 0 ? null : <span className="text-sm text-ink-muted">{facts.tags.join(" · ")}</span>}
        <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink-muted">
          {facts.eta === null ? null : (
            <span className="inline-flex items-center gap-1 font-semibold text-ink">
              <IconClock size={16} /> {facts.eta}
            </span>
          )}
          {facts.fee === null ? null : (
            <span className="inline-flex items-center gap-1">
              <IconBike size={16} />
              {facts.fee.amount === 0 ? "Livrare gratuită" : `livrare ${facts.fee.from ? "de la " : ""}${formatRon(facts.fee.amount)}`}
            </span>
          )}
          {facts.minOrder === null || facts.minOrder <= 0 ? null : <span>{`minim ${formatRon(facts.minOrder)}`}</span>}
        </span>
        {coverageState === "loading" ? (
          <span className="da-shimmer block h-3.5 w-1/3 rounded" aria-hidden="true" />
        ) : facts.coverage === "deliverable" ? (
          <span className="text-sm font-bold text-success">✓ Livrăm la adresa ta</span>
        ) : facts.coverage === "out_of_coverage" ? (
          <span className="text-sm font-bold text-warning">Adresa ta este în afara zonei de livrare</span>
        ) : facts.coverage === "no_vendor" ? (
          <span className="text-sm font-bold text-warning">Momentan nu putem livra la adresa ta</span>
        ) : null}
        <span className="mt-1 inline-flex items-center gap-1 self-start rounded-control bg-brand px-4 py-2.5 text-base font-bold text-brand-contrast">
          Vezi meniul <IconChevronRight size={18} />
        </span>
      </div>
    </button>
  );
}

export function StorefrontPicker({
  storefronts,
  coverageFor,
  coverageState,
  onSelect,
  layout = "grila",
  columns = "2",
}: {
  storefronts: StorefrontSummaryDto[];
  coverageFor: (slug: string) => StorefrontCoverageEntryDto | null;
  coverageState: "unknown" | "loading" | "ready";
  onSelect: (slug: string) => void;
  /** `grila` (implicit), `carusel` (derulare pe orizontala) sau `lista` (un rand). */
  layout?: string;
  /** Cate carduri pe rand in modul grila: „2", „3" sau „4". */
  columns?: string;
}) {
  if (storefronts.length === 0) {
    return (
      <p className="rounded-card border border-line bg-surface p-4 text-sm text-ink">
        Momentan nu putem prelua comenzi. Revino mai târziu.
      </p>
    );
  }

  const carousel = layout === "carusel";
  const kind: "grila" | "carusel" | "lista" = carousel ? "carusel" : layout === "lista" ? "lista" : "grila";
  const listClass = carousel
    ? "da-rail da-rail-bleed snap-x pb-2 [&>li]:w-72 [&>li]:snap-start"
    : kind === "lista"
      ? "grid gap-3"
      : `grid gap-3 sm:gap-4 ${GRID_COLUMNS[columns] ?? GRID_COLUMNS["2"]}`;

  return (
    <ul className={listClass}>
      {storefronts.map((storefront, index) => (
        <StorefrontCard
          key={storefront.id}
          storefront={storefront}
          coverage={coverageFor(storefront.slug)}
          coverageState={coverageState}
          onSelect={onSelect}
          priority={index === 0}
          layout={kind}
        />
      ))}
    </ul>
  );
}
